import React, { useState, useRef, useEffect, useCallback } from 'react';
import './vessels.css'
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatInTimeZone } from 'date-fns-tz';
import { Company, Status, Vessels } from '../../interfaces/interfaces';
import { deleteVessel, getCompany, getStatus, getVessel, patchVessel, postVessel } from '../../services/requests';
import Table, { ColumnConfig } from '../../components/table/table';


const VesselsComponent: React.FC = () => {
    const nempresa = Cookies.get('Nempresa');
    const [loading, setLoading] = useState(true);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [dataStatus, setDataStatus] = useState<Status[]>([]);
    const [filteredDataStatus, setFilteredDataStatus] = useState(dataStatus);
    const [dataCompany, setDataCompany] = useState<Company[]>([]);
    const [filteredDataCompany, setFilteredDataCompany] = useState(dataCompany);
    const [minDate, setMinDate] = useState("");
    const { t } = useTranslation();
    const [data, setData] = useState([{ Name: "", ID: "", Type: "", Year: "", RegistrationDate: "", Size: "", IDregistro: "", Photo: "", Company: "", Cargaje: "" }]);
    const menuTypeRef = useRef<HTMLDivElement | null>(null);
    const menuCompanyRef = useRef<HTMLDivElement | null>(null);

    /**
     * Formatea una fecha ISO a "YYYY-MM-DD HH:MM:SS".
     * @param isoString - Fecha en formato ISO (ej: "2024-04-29T14:35:00Z").
     * @returns Fecha formateada como string.
     */
    const formatDate = (isoString: string): string => {
        const timeZone = 'Europe/Madrid';
        return formatInTimeZone(isoString, timeZone, 'yyyy-MM-dd HH:mm:ss');
    };

    /**
     * Obtiene datos de embarcaciones, estados y empresas desde la API.
     * Aplica filtros por tipo "Eb" y por número de empresa (`nempresa`).
     * Los datos son formateados y almacenados en el estado del componente.
     */
    const getInformation = useCallback(async () => {
        try {
            setLoading(true);

            const responseVessel = await getVessel();

            const responseStatus = await getStatus();

            const responseCompany = await getCompany();

            const filteredStatus = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Eb");

            setDataStatus(filteredStatus);
            setFilteredDataStatus(filteredStatus);

            const filteredCompany = (responseCompany.data.data as Company[]).filter(company => nempresa === '' || company.Nempresa === Number(nempresa))
            setDataCompany(filteredCompany);
            setFilteredDataCompany(filteredCompany);

            const transformedData = (responseVessel.data.data as Vessels[])
                .filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
                .map(vessel => {
                    const statusMatch = (responseStatus.data.data as Status[]).find(item => item.Nestado === vessel.Tipo);
                    const companyMatch = (responseCompany.data.data as Company[]).find(item => item.Nempresa === vessel.Nempresa);

                    return {
                        Name: vessel.Tembarcacion ? vessel.Tembarcacion : "",
                        ID: vessel.Nembarcacion ? vessel.Nembarcacion.toString() : "",
                        IDregistro: vessel.IDregistro ? vessel.IDregistro : "",
                        Type: statusMatch ? statusMatch.Testado : "",
                        Year: vessel.AManufactura ? vessel.AManufactura.toString() : "",
                        RegistrationDate: vessel.Ffabricacion ? formatDate(vessel.Ffabricacion.toString()) : "",
                        Size: vessel.Tamanno ? vessel.Tamanno.toString() : "",
                        Photo: vessel.Foto ? vessel.Foto : "",
                        Company: companyMatch ? companyMatch.Tempresa : "",
                        Cargaje: vessel.Cargaje ? vessel.Cargaje.toString() : "",
                    };
                });

            setData(transformedData);
            setFilteredData(transformedData);

        } catch (error) {
            toast.error(`Error: ${error}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [nempresa]);

    /**
     * Al montar el componente, carga la información y define la fecha mínima y actual en formato UTC.
     */
    useEffect(() => {
        getInformation();

        const nowUTC = new Date().toISOString().slice(0, 19);
        setMinDate(nowUTC);
        setModalDate(nowUTC);
    }, [getInformation]);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const totalPage = itemsPerPage === filteredData.length ? 1 : Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === filteredData.length ? filteredData.length : startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);
    const ability = useAbility();

    /**
     * Avanza a la siguiente página si no se ha alcanzado la última.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Retrocede a la página anterior si no se está en la primera.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Descarga los datos actuales como archivo Excel (.xlsx).
     */
    const downloadDataAsXLSX = () => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Vessels Data');
        XLSX.writeFile(workbook, 'Vessels Data.xlsx');
    };

    /**
     * Filtra los datos según el término de búsqueda y actualiza la página actual.
     */
    const handleSearch = () => {
        const filtered = data.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
        setFilteredData(filtered);

        if (filtered.length > 0) {
            setCurrentPage(1);
        } else {
            setCurrentPage(0);
        }
    };

    const [modalNameNew, setModalNameNew] = useState('');
    const [modalRecordIDNew, setModalRecordIDNew] = useState('');
    const [modalTypeNew, setModalTypeNew] = useState('');
    const [modalYearNew, setModalYearNew] = useState('');
    const [modalDateNew, setModalDateNew] = useState('');
    const [modalCompanyNew, setModalCompanyNew] = useState('');
    const [modalSizeNew, setModalSizeNew] = useState('');
    const [modalPermissibleLoadNew, setModalPermissibleLoadNew] = useState('');

    const [showModalNew, setShowModalNew] = useState(false);
    const [modalName, setModalName] = useState('');
    const [modalRecordID, setModalRecordID] = useState('');
    const [modalType, setModalType] = useState('');
    const [modalYear, setModalYear] = useState('');
    const [modalDate, setModalDate] = useState('');
    const [modalCompany, setModalCompany] = useState('');
    const [modalSize, setModalSize] = useState('');
    const [modalPermissibleLoad, setModalPermissibleLoad] = useState('');

    /**
     * Abre el modal para crear un nuevo registro.
     */
    const handleCardClickNew = () => {
        setShowModalNew(true);
    };

    /**
     * Cierra todos los modales relacionados con la gestión de embarcaciones.
     * - Restablece los valores de los campos del formulario a su estado inicial.
     * - Reinicia los estados de error y carga (`loadingBtn`).
     * - Establece la fecha por defecto (`minDate`) en `ModalDate`.
     */
    const closeModal = () => {
        setShowModalNew(false);
        setShowModalEdit(false);
        setShowModalDelete(false);
        setSelectedID('');
        setModalID('');
        setModalName('');
        setModalType('');
        setModalYear('');
        setModalDate('');
        setModalSize('');
        setModalRecordID('');
        setModalCompany('');
        setModalDate(minDate);
        setLoadingBtn(false);
        setErrorName('');
        setErrorRecordID('');
        setErrorType('');
        setErrorYear('');
        setErrorRegistrationDate('');
        setErrorCompany('');
        setErrorSize('');
        setErrorPermissibleLoad('');
    };

    /**
     * Maneja el clic en el overlay (fondo del modal) o el ícono de cierre.
     * Cierra todos los modales y resetea los estados del formulario.
     * 
     * @param e El evento de clic en el overlay o el ícono de cierre.
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay') || (e.target as HTMLElement).classList.contains('closeIcon')) {
            closeModal();
        }
    };

    /**
     * Extrae un mensaje de error desde un campo recibido.
     * - Si el campo es un array, retorna el primer elemento.
     * - Si el campo es un string, lo retorna directamente.
     * - En cualquier otro caso, retorna una cadena vacía.
     * 
     * @param field - Campo que puede ser un array, string u otro tipo.
     * @returns El mensaje de error como string.
     */
    const extractErrorMessage = (field: any) => {
        if (Array.isArray(field)) return field[0];
        if (typeof field === 'string') return field;
        return '';
    };

    /**
     * Maneja el proceso de agregar un nuevo barco (vessel).
     * Verifica que todos los campos del formulario estén completos. Si algún campo está vacío, muestra un mensaje de error.
     * Si los campos están completos, agrega el nuevo barco tanto al estado local como al servidor mediante una solicitud HTTP POST.
     * Después de agregar el barco, restablece los campos del formulario y cierra el modal de adición.
     * 
     * @throws {Error} Lanza un error si la solicitud al servidor falla.
     */
    const handleAddVessel = async () => {
        setLoadingBtn(true);

        if (!modalNameNew || !modalRecordIDNew || !modalTypeNew || !modalDateNew || !modalCompanyNew) {
            setErrorName(!modalNameNew ? "Por favor complete este campo" : "");
            setErrorRecordID(!modalRecordIDNew ? "Por favor complete este campo" : "");
            setErrorType(!modalTypeNew ? "Por favor complete este campo" : "");
            setErrorRegistrationDate(!modalDateNew ? "Por favor complete este campo" : "");
            setErrorCompany(!modalCompanyNew ? "Por favor complete este campo" : "");
            toast.error(`${t('vessels.pleaseCompleteAllFields')}`);
            setLoadingBtn(false);
            return;
        }

        try {
            const currentDate = new Date().toISOString().slice(0, 19) + "Z";
            const companyMatch = dataCompany.find(item => item.Tempresa === modalCompanyNew);
            const statusMatch = dataStatus.find(item => item.Testado === modalTypeNew);

            const body = {
                Tembarcacion: modalNameNew,
                IDregistro: modalRecordIDNew,
                Ffabricacion: modalDateNew ? modalDateNew : '',
                AManufactura: isNaN(Number(modalYearNew)) ? '' : Number(modalYearNew),
                Tamanno: isNaN(Number(modalSizeNew)) ? '' : Number(modalSizeNew),
                Falta: currentDate,
                Nempresa: companyMatch?.Nempresa,
                Tipo: statusMatch?.Nestado,
                Cargaje: isNaN(Number(modalPermissibleLoadNew)) ? '' : Number(modalPermissibleLoadNew),
            };

            const response = await postVessel(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('vessels.theVesselWithIDCouldNotBeAdded')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('vessels.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorName(extractErrorMessage(errorData.Tembarcacion));
            setErrorRecordID(extractErrorMessage(errorData.IDregistro));
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorYear(extractErrorMessage(errorData.AManufactura));
            setErrorRegistrationDate(extractErrorMessage(errorData.Ffabricacion));
            setErrorCompany(extractErrorMessage(errorData.Nempresa));
            setErrorSize(extractErrorMessage(errorData.Tamanno));
            setErrorPermissibleLoad(extractErrorMessage(errorData.Cargaje));

            toast.error(`${message}`, {
                position: "top-right",
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof data[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "Name", direction: "asc" });

    // Ordena los datos por nombre (Name) cada vez que cambia 'data'
    // y actualiza el estado 'filteredData' con el resultado ordenado.
    useEffect(() => {
        const sortedData = [...data].sort((a, b) => a.Name.localeCompare(b.Name));
        setFilteredData(sortedData);
    }, [data]);

    // Ordena los datos por la clave dada ('key') en orden ascendente o descendente,
    // alternando la dirección si se vuelve a hacer clic sobre la misma clave.
    // Actualiza tanto la configuración de orden ('sortConfig') como el estado 'filteredData'.
    const sortData = (key: keyof typeof data[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sortedData = [...data].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setFilteredData(sortedData);
    };

    const [showModalEdit, setShowModalEdit] = useState(false);
    const [modalID, setModalID] = useState('');
    const [selectedID, setSelectedID] = useState('');

    // Actualiza una embarcación seleccionada utilizando los valores de los campos del modal.
    // Valida los campos requeridos, actualiza el estado local y envía la modificación al backend mediante PATCH.
    // Requiere que 'selectedID' esté definido y todos los campos del modal estén completos.
    const handleUpdateVessel = async () => {
        if (!selectedID) return;

        setLoadingBtn(true);

        if (!modalName || !modalRecordID || !modalType || !modalCompany) {
            setErrorName(!modalName ? "Por favor complete este campo" : "");
            setErrorRecordID(!modalRecordID ? "Por favor complete este campo" : "");
            setErrorType(!modalType ? "Por favor complete este campo" : "");
            setErrorCompany(!modalCompany ? "Por favor complete este campo" : "");
            toast.error(`${t('vessels.pleaseCompleteAllFields')}`);
            setLoadingBtn(false);
            return;
        }

        try {
            const statusMatch = dataStatus.find(item => item.Testado === modalType);
            const companyMatch = dataCompany.find(item => item.Tempresa === modalCompany);

            const body = {
                id: modalID,
                Tembarcacion: modalName,
                IDregistro: modalRecordID,
                Ffabricacion: modalDate ? modalDate : '',
                AManufactura: isNaN(Number(modalYear)) ? '' : Number(modalYear),
                Tamanno: isNaN(Number(modalSize)) ? '' : Number(modalSize),
                Nempresa: companyMatch?.Nempresa,
                Tipo: statusMatch?.Nestado,
                Cargaje: isNaN(Number(modalPermissibleLoad)) ? '' : Number(modalPermissibleLoad),
            };

            const response = await patchVessel(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('vessels.failedToEditVesselWithID')}: ${selectedID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('vessels.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorName(extractErrorMessage(errorData.Tembarcacion));
            setErrorRecordID(extractErrorMessage(errorData.IDregistro));
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorYear(extractErrorMessage(errorData.AManufactura));
            setErrorRegistrationDate(extractErrorMessage(errorData.Ffabricacion));
            setErrorCompany(extractErrorMessage(errorData.Nempresa));
            setErrorSize(extractErrorMessage(errorData.Tamanno));
            setErrorPermissibleLoad(extractErrorMessage(errorData.Cargaje));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [showModalDelete, setShowModalDelete] = useState(false);

    // Elimina una embarcación seleccionada mediante una petición DELETE al backend.
    // Requiere que 'selectedID' y 'modalID' estén definidos.
    // Actualiza el estado local para reflejar los cambios tras una eliminación exitosa o fallida.
    const handleDeleteVessel = async () => {
        if (!selectedID) return;

        setLoadingBtn(true);

        try {
            const data = {
                id: modalID
            };

            const response = await deleteVessel(data);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.success(`${t('vessels.theVesselWithIDCouldNotBeRemoved')}: ${selectedID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('vessels.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorName(extractErrorMessage(errorData.Tembarcacion));
            setErrorRecordID(extractErrorMessage(errorData.IDregistro));
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorYear(extractErrorMessage(errorData.AManufactura));
            setErrorRegistrationDate(extractErrorMessage(errorData.Ffabricacion));
            setErrorCompany(extractErrorMessage(errorData.Nempresa));
            setErrorSize(extractErrorMessage(errorData.Tamanno));
            setErrorPermissibleLoad(extractErrorMessage(errorData.Cargaje));

            toast.error(`${message}`, {
                position: "top-right",
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    // Cierra los dropdowns si el usuario hace clic fuera de los menús.
    // Requiere refs: menuTypeRef y menuCompanyRef. Se limpia el listener al desmontar.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuTypeRef.current &&
                event.target instanceof Node &&
                !menuTypeRef.current.contains(event.target)
            ) { }

            if (
                menuCompanyRef.current &&
                event.target instanceof Node &&
                !menuCompanyRef.current.contains(event.target)
            ) { }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const [isSelectOpenType, setIsSelectOpenType] = useState(false);
    const handleSelectFocusType = () => setIsSelectOpenType(true);
    const handleSelectBlurType = () => setIsSelectOpenType(false);

    const [isSelectOpenCompany, setIsSelectOpenCompany] = useState(false);
    const handleSelectFocusCompany = () => setIsSelectOpenCompany(true);
    const handleSelectBlurCompany = () => setIsSelectOpenCompany(false);

    const [errorName, setErrorName] = useState('');
    const [errorRecordID, setErrorRecordID] = useState('');
    const [errorType, setErrorType] = useState('');
    const [errorYear, setErrorYear] = useState('');
    const [errorRegistrationDate, setErrorRegistrationDate] = useState('');
    const [errorCompany, setErrorCompany] = useState('');
    const [errorSize, setErrorSize] = useState('');
    const [errorPermissibleLoad, setErrorPermissibleLoad] = useState('');

    const columns: ColumnConfig[] = [
        { key: 'Name', label: t('vessels.name'), sortable: true },
        { key: 'ID', label: "#IMO", sortable: true },
        { key: 'Type', label: t('vessels.type'), sortable: true },
        { key: 'RegistrationDate', label: t('vessels.registrationDate'), sortable: true },
        { key: 'Cargaje', label: t('vessels.permissibleLoad'), sortable: true },
        { key: 'Size', label: t('vessels.size'), sortable: true },
    ];

    return (
        <div className='containerVessels'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('vessels.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('vessels.vessels')}</h4>
                        <div>
                            {(ability.can('create', 'AdminPage') || ability.can('create', 'ManagerPage')) && (
                                <button className="btn btn-dark" onClick={handleCardClickNew}>
                                    <i className="fas fa-plus"></i> {t('vessels.newVessel')}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={`separator ${isFullScreen ? 'expanded' : ''}`}>
                        <Table
                            t={t}
                            titleKey="vessels.vesselsList"
                            placeholder='vessels.searchVessels'
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            handleSearch={handleSearch}
                            downloadDataAsXLSX={downloadDataAsXLSX}
                            isFullScreen={isFullScreen}
                            setIsFullScreen={setIsFullScreen}

                            nameClassTable="tableVessels"
                            columns={columns}
                            currentData={currentData}
                            sortConfig={sortConfig}
                            onSort={sortData}
                            onRowEdit={(row) => {
                                setShowModalEdit(true);
                                setSelectedID(row.ID);
                                setModalID(row.ID);
                                const selectedItem = filteredData.find((data) => data.ID === row.ID);
                                setModalName(selectedItem ? selectedItem.Name : "");
                                setModalRecordID(selectedItem ? selectedItem.IDregistro : "");
                                setModalType(selectedItem ? selectedItem.Type : "");
                                setModalYear(selectedItem ? selectedItem.Year : "");
                                setModalDate(selectedItem ? selectedItem.RegistrationDate : "");
                                setModalCompany(selectedItem ? selectedItem.Company : "");
                                setModalSize(selectedItem ? selectedItem.Size : "");
                                setModalPermissibleLoad(selectedItem ? selectedItem.Cargaje : "");
                            }}
                            onRowDelete={(row) => {
                                setShowModalDelete(true);
                                setSelectedID(row.ID);
                                setModalID(row.ID);
                                const selectedItem = filteredData.find((data) => data.ID === row.ID);
                                setModalName(selectedItem ? selectedItem.Name : "");
                                setModalRecordID(selectedItem ? selectedItem.IDregistro : "");
                                setModalType(selectedItem ? selectedItem.Type : "");
                                setModalYear(selectedItem ? selectedItem.Year : "");
                                setModalDate(selectedItem ? selectedItem.RegistrationDate : "");
                                setModalCompany(selectedItem ? selectedItem.Company : "");
                                setModalSize(selectedItem ? selectedItem.Size : "");
                                setModalPermissibleLoad(selectedItem ? selectedItem.Cargaje : "");
                            }}

                            handlePreviousPage={handlePreviousPage}
                            currentPage={currentPage}
                            totalPage={totalPage}
                            itemsPerPage={itemsPerPage}
                            filteredData={filteredData}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            handleNextPage={handleNextPage}
                            data={data}
                        />
                    </div>

                    {(showModalNew || showModalEdit || showModalDelete) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>
                                        {showModalNew ? t('vessels.newVessel') : showModalEdit ? t('vessels.editVessel') : showModalDelete ? t('vessels.deleteVessel') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={handleOverlayClick}>&times;</span>
                                </div>
                                {!showModalNew &&
                                    <div className='formInput'>
                                        <div className="input-row">
                                            <h6>ID</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalID}
                                                placeholder="Search by ID"
                                                disabled
                                            />
                                        </div>
                                    </div>
                                }
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('vessels.name')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorName ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalNameNew : modalName}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalNameNew(e.target.value); }
                                                if (!showModalNew) { setModalName(e.target.value); }
                                                setErrorName('');
                                            }}
                                            placeholder={`${t('vessels.enterThe')} ${t('vessels.name').toLowerCase()}`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorName && (
                                        <div className="invalid-feedback d-block">{errorName}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> #IMO</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorRecordID ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalRecordIDNew : modalRecordID}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalRecordIDNew(e.target.value); }
                                                if (!showModalNew) { setModalRecordID(e.target.value); }
                                                setErrorRecordID('');
                                            }}
                                            placeholder={`${t('vessels.enterThe')} #IMO`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorRecordID && (
                                        <div className="invalid-feedback d-block">{errorRecordID}</div>
                                    )}
                                </div>
                                <div className='formInput' ref={menuTypeRef}>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('vessels.type')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorType ? 'is-invalid' : ''}`}
                                                value={showModalNew ? modalTypeNew : modalType}
                                                onChange={(e) => {
                                                    if (showModalNew) { setModalTypeNew(e.target.value); }
                                                    if (!showModalNew) { setModalType(e.target.value); }
                                                    setErrorType('');
                                                }}
                                                onFocus={handleSelectFocusType}
                                                onBlur={handleSelectBlurType}
                                                disabled={showModalDelete}
                                            >
                                                <option value="">{`${t('vessels.selectA2')} ${t('vessels.type').toLowerCase()}`} ...</option>
                                                {filteredDataStatus
                                                    .map((item) => (
                                                        <option key={item.Nestado} value={String(item.Testado)}>
                                                            {item.Testado}
                                                        </option>
                                                    ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenType ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorType && (
                                        <div className="invalid-feedback d-block">{errorType}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('vessels.year')} {t('vessels.manufacturing')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorYear ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalYearNew : modalYear}
                                            maxLength={4}
                                            pattern="\d{4}"
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*$/.test(value) && value.length <= 4) {
                                                    if (showModalNew) { setModalYearNew(value); }
                                                    if (!showModalNew) { setModalYear(value); }
                                                }
                                                setErrorYear('');
                                            }}
                                            placeholder={`${t('vessels.enterThe')} ${t('vessels.year').toLowerCase()} ${t('vessels.manufacturing')}`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorYear && (
                                        <div className="invalid-feedback d-block">{errorYear}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('vessels.registrationDate')} (UTC)</h6>
                                        <DatePicker
                                            selected={showModalNew ? modalDateNew ? new Date(modalDateNew) : null : modalDate ? new Date(modalDate) : null}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    if (showModalNew) { setModalDateNew(date.toISOString()); }
                                                    if (!showModalNew) { setModalDate(date.toISOString()); }
                                                } else {
                                                    setModalDateNew("");
                                                }
                                                setErrorRegistrationDate('');
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm:ss"
                                            timeIntervals={1}
                                            dateFormat="dd/MM/yyyy, HH:mm:ss"
                                            timeCaption="Time"
                                            className={`form-control ${errorRegistrationDate ? 'is-invalid' : ''}`}
                                            maxDate={new Date()}
                                            placeholderText={`${t('vessels.selectA1')} ${t('vessels.registrationDate').toLowerCase()} (UTC)`}
                                            disabled={showModalDelete}
                                            portalId="root-portal"
                                            popperPlacement="bottom-start"
                                            popperClassName="custom-datepicker-popper"
                                        />
                                    </div>
                                    {errorRegistrationDate && (
                                        <div className="invalid-feedback d-block">{errorRegistrationDate}</div>
                                    )}
                                </div>
                                <div className='formInput' ref={menuCompanyRef}>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('vessels.company')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorCompany ? 'is-invalid' : ''}`}
                                                value={showModalNew ? modalCompanyNew : modalCompany}
                                                onChange={(e) => {
                                                    if (showModalNew) { setModalCompanyNew(e.target.value); }
                                                    if (!showModalNew) { setModalCompany(e.target.value); }
                                                    setErrorCompany('');
                                                }}
                                                onFocus={handleSelectFocusCompany}
                                                onBlur={handleSelectBlurCompany}
                                                disabled={showModalDelete}
                                            >
                                                <option value="">{`${t('vessels.selectA2')} ${t('vessels.company').toLowerCase()}`} ...</option>
                                                {filteredDataCompany
                                                    .map((item) => (
                                                        <option key={item.Nempresa} value={String(item.Tempresa)}>
                                                            {item.Tempresa}
                                                        </option>
                                                    ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenCompany ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorCompany && (
                                        <div className="invalid-feedback d-block">{errorCompany}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('vessels.size')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorSize ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalSizeNew : modalSize}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*\.?\d*$/.test(value)) {
                                                    if (showModalNew) { setModalSizeNew(value); }
                                                    if (!showModalNew) { setModalSize(value); }
                                                }
                                                setErrorSize('');
                                            }}
                                            placeholder={`${t('vessels.enterThe')} ${t('vessels.boatLength')} [m]`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorSize && (
                                        <div className="invalid-feedback d-block">{errorSize}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('vessels.permissibleLoad')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorPermissibleLoad ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalPermissibleLoadNew : modalPermissibleLoad}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*\.?\d*$/.test(value)) {
                                                    if (showModalNew) { setModalPermissibleLoadNew(value); }
                                                    if (!showModalNew) { setModalPermissibleLoad(value); }
                                                }
                                                setErrorPermissibleLoad('');
                                            }}
                                            placeholder={`${t('vessels.enterThe2')} ${t('vessels.permissibleLoad').toLowerCase()}`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorPermissibleLoad && (
                                        <div className="invalid-feedback d-block">{errorPermissibleLoad}</div>
                                    )}
                                </div>
                                <div className='formBtn'>
                                    <button className={showModalDelete ? 'btn btn-danger' : 'btn btn-dark'} onClick={() => {
                                        if (showModalNew) handleAddVessel();
                                        if (showModalEdit) handleUpdateVessel();
                                        if (showModalDelete) handleDeleteVessel();
                                    }}
                                        disabled={loadingBtn}>
                                        {loadingBtn ? (
                                            <div className="loading-btn">
                                                <ClipLoader color="#007bff" size={20} />
                                            </div>
                                        ) : (
                                            showModalNew ? t('vessels.addVessel') : showModalEdit ? t('vessels.updateVessel') : showModalDelete ? t('vessels.deleteVessel') : ''
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div >
    );
};

export default VesselsComponent;